import Dexie, { type Table } from 'dexie'
import type { Race } from '@/graphql/types/race'
import type { RacePerson } from '@/graphql/types/race_person';
import type { RaceTeam } from '@/graphql/types/race_team';
import type { RaceCategory } from '@/graphql/types/race_category';
import type { RaceDiscipline } from '@/graphql/types/race_discipline';

function setTime(date: Date, time: number) {
  const millis = time*10;
  const hours = Math.floor(millis / (1000 * 60 * 60));
  const minutes = Math.floor((millis % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((millis % (1000 * 60)) / 1000);
  const d = new Date(date);
  d.setHours(hours, minutes, seconds, 0);
  return d;
}

// Extend the existing types with sync metadata
export interface LocalRace extends Omit<Race, 'people' | 'dates' | 'startAt' | 'updatedAt'> {
  dates: Date[];
  startAt: Date;
  updatedAt: Date;
  personIds: number[];
  people?: LocalRacePerson[];
}

export interface LocalRacePerson extends Omit<RacePerson, 'team' | 'category' | 'discipline' | 'birthDate' | 'startDate' | 'startTime' | 'warmupTime' | 'entryTime'> {
  raceId?: number;
  race?: LocalRace;
  teamId: number;
  team?: RaceTeam;
  categoryId: number;
  category?: RaceCategory;
  disciplineId: number;
  discipline?: RaceDiscipline;
  birthDate: Date;
  startDate: Date;
  startTime: Date;
  warmupTime: Date;
  entryTime: Date;
}

export function castRace(race: Race): LocalRace {
  return {
    ...race,
    id: parseInt(`${race.id}`),
    dates: race.dates.map(date => new Date(date)),
    startAt: new Date(race.startAt),
    updatedAt: new Date(race.updatedAt),
    personIds: race.people.map(person => parseInt(`${person.id}`)),
    people: undefined
  };
}

export function castRacePerson(person: RacePerson): LocalRacePerson {
  const startDate = new Date(person.startDate);
  const result: LocalRacePerson = {
    ...person,
    id: parseInt(`${person.id}`),
    teamId: parseInt(`${person.team.id}`),
    categoryId: parseInt(`${person.category.id}`),
    disciplineId: parseInt(`${person.discipline.id}`),
    birthDate: new Date(person.birthDate),
    startDate: startDate,
    startTime: setTime(startDate, person.startTime),
    warmupTime: setTime(startDate, person.warmupTime),
    entryTime: setTime(startDate, person.entryTime)
  };

  delete result.team;
  delete result.category;
  delete result.discipline;
  return result;
}


export class RacesDB extends Dexie {
  races!: Table<LocalRace>;
  people!: Table<LocalRacePerson>;
  teams!: Table<RaceTeam>;
  categories!: Table<RaceCategory>;
  disciplines!: Table<RaceDiscipline>;

  constructor() {
    super('RacesDB');

    this.version(1).stores({
      races: `
        ++id,
        name,
        startAt,
        synced,
        dates
      `,

      people: `
        ++id,
        raceId,
        startIndex,
        startDate,
        [raceId+id]
      `,

      teams: '++id',
      categories: '++id, code',
      disciplines: '++id, code'
    });
  }

  getUpcomingRaces(): Promise<LocalRace[]> {
    return this.races.where('startAt').above(new Date()).toArray();
  }

  // Race queries
  async getTodaysRaces(): Promise<LocalRace[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.races
      .where('dates')
      .equals([today])
      .toArray();
  }

  async getRaceWithPeople(raceId: string | number): Promise<LocalRace | undefined> {
    const race = await this.races.get(parseInt(`${raceId}`));
    if (!race) return undefined;

    const ids = race.personIds || []
    const people = await this.people
      .where('id')
      .anyOf(ids)
      .sortBy('startIndex');

    race.people = people;
    return race;
  }

  async getRaceWithDetails(raceId: string | number): Promise<LocalRace | undefined> {
    const race = await this.getRaceWithPeople(raceId);
    if (!race) return undefined;

    // Get all unique team, category, and discipline IDs from participants
    const teamIds = new Set(race.people?.map(p => p.teamId));
    const categoryIds = new Set(race.people?.map(p => p.categoryId));
    const disciplineIds = new Set(race.people?.map(p => p.disciplineId));

    // Fetch all related data in parallel
    const [teams, categories, disciplines] = await Promise.all([
      this.teams.where('id').anyOf([...teamIds]).toArray(),
      this.categories.where('id').anyOf([...categoryIds]).toArray(),
      this.disciplines.where('id').anyOf([...disciplineIds]).toArray(),
    ]);

    // Create lookup maps for faster access
    const teamMap = new Map(teams.map(t => [t.id, t]));
    const categoryMap = new Map(categories.map(c => [c.id, c]));
    const disciplineMap = new Map(disciplines.map(d => [d.id, d]));

    // Attach all related data to participants
    const enrichedPeople = race.people?.map(person => ({
      ...person,
      team: teamMap.get(person.teamId),
      category: categoryMap.get(person.categoryId),
      discipline: disciplineMap.get(person.disciplineId),
    }));

    race.people = enrichedPeople;
    return race;
  }

  async getPersonWithDetails(personId: string | number): Promise<LocalRacePerson | undefined> {
    const person = await this.people.get(parseInt(`${personId}`));
    if (!person) return undefined;

    const race = await this.races.get(person.raceId);
    if (race) {
      person.race = race;
    }

    const team = await this.teams.get(person.teamId);
    if (team) {
      person.team = team;
    }

    const category = await this.categories.get(person.categoryId);
    if (category) {
      person.category = category;
    }

    const discipline = await this.disciplines.get(person.disciplineId);
    if (discipline) {
      person.discipline = discipline;
    }

    return person;
  }
}

// Export a single instance
export const db = new RacesDB();
